/* eslint-disable no-unused-vars */
import { useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectBrands_audience_to_save,
  selectAllBrandsModels,
  selectTargettingErrorFlag,
} from 'store/selectors';
import {
  generalActions,
  targetingBrandsModelsActions,
  targetingPlacementsActions,
} from 'store/actions';

export const useFacade = (selectedType) => {
  const dispatch = useDispatch();
  const { inc, exc } = useSelector(selectBrands_audience_to_save);
  const allBrandsModels = useSelector(selectAllBrandsModels);

  const initExcModels = useMemo(
    () => (exc ? exc?.split(',').map((model) => Number(model.split(':')[1])) : []),
    [exc],
  );
  const initIncBrandsModels = useMemo(
    () =>
      inc
        ? inc?.split(',').map((modelOrBrand) => {
            if (modelOrBrand.split(':')[0] === 'Brand') return modelOrBrand.split(':')[1];
            if (modelOrBrand.split(':')[0] === 'Model') return Number(modelOrBrand.split(':')[1]);
          })
        : [],
    [inc],
  );

  const convertDataToSave = useCallback((set) => {
    const arr = [...set];
    let res = arr.map((item) => {
      const isCounter = typeof item === 'number';
      if (!isCounter) return `Brand:${item}`;
      if (isCounter) return `Model:${item}`;
      return;
    });
    return res.toString();
  }, []);

  const brandToCounters = useMemo(
    () =>
      Object.fromEntries(
        allBrandsModels.map(({ brand, models }) => [brand, models.map((m) => m.counter)]),
      ),
    [allBrandsModels],
  );

  const deleteIncAction = useCallback(
    (brandOrCounter) => {
      const isCounter = typeof brandOrCounter === 'number';
      if (isCounter) {
        const newIncData = initIncBrandsModels.filter(
          (modelOrBrand) => modelOrBrand !== +brandOrCounter,
        );
        const dataToSave = {
          inc: convertDataToSave(newIncData),
          exc,
        };
        dispatch(
          generalActions.updateCurrentCampaignObj({
            propField: 'brands_audience_to_save',
            newValue: dataToSave,
          }),
        );
      }
      if (!isCounter) {
        const newExcModels = initExcModels.filter(
          (counter) => !brandToCounters[brandOrCounter].includes(counter),
        );

        const newIncData = initIncBrandsModels.filter(
          (brandOrModel) => brandOrModel !== brandOrCounter,
        );
        const dataToSave = {
          inc: convertDataToSave(newIncData),
          exc: convertDataToSave(newExcModels),
        };
        dispatch(
          generalActions.updateCurrentCampaignObj({
            propField: 'brands_audience_to_save',
            newValue: dataToSave,
          }),
        );
      }
    },
    [initIncBrandsModels, initExcModels],
  );

  const deleteExcAction = (counter) => {
    const newExcData = initExcModels.filter((model) => model !== +counter);
    const dataToSave = {
      inc,
      exc: convertDataToSave(newExcData),
    };
    dispatch(
      generalActions.updateCurrentCampaignObj({
        propField: 'brands_audience_to_save',
        newValue: dataToSave,
      }),
    );
  };

  const counterOrBrandToData = useMemo(
    () =>
      Object.fromEntries(
        allBrandsModels.flatMap((brandData) => [
          [brandData.brand, brandData],
          ...brandData.models.map((model) => [model.counter, model]),
        ]),
      ),
    [allBrandsModels],
  );

  const clearAllExc = useCallback(
    (id) => {
      if (id === 'inc_clear_all') {
        dispatch(
          generalActions.updateCurrentCampaignObj({
            propField: 'brands_audience_to_save',
            newValue: { inc: '', exc: '' },
          }),
        );
      }
      if (id === 'exc_clear_all') {
        const dataToSave = {
          inc,
          exc: '',
        };
        dispatch(
          generalActions.updateCurrentCampaignObj({
            propField: 'brands_audience_to_save',
            newValue: dataToSave,
          }),
        );
      }
    },
    [inc],
  );
  const errorFlag = useSelector(selectTargettingErrorFlag);

  const handleChange = useCallback(
    (newTab) => {
      if (newTab === selectedType) return;
      if (inc) {
        clearAllExc('inc_clear_all');
      }
      dispatch(targetingBrandsModelsActions.changeBrandsModelsTargetingType(newTab));
      if (errorFlag.devices && newTab !== selectedType) {
        dispatch(
          targetingPlacementsActions.updateErrorField({ errorField: 'devices', newValue: false }),
        );
      }
    },
    [errorFlag.devices, clearAllExc, selectedType],
  );

  useEffect(() => {
    if (inc) {
      dispatch(
        targetingBrandsModelsActions.changeBrandsModelsTargetingType('specific_brands_models'),
      );
    }
  }, [inc]);

  return {
    deleteIncAction,
    deleteExcAction,
    initExcModels,
    initIncBrandsModels,
    counterOrBrandToData,
    clearAllExc,
    handleChange,
  };
};
